// Mapa de calor Parcelas × Taxa a partir das linhas do /Quote/report.
// A taxa efetiva de cada transação = (totalValue - compensationValue) / totalValue,
// ou seja, o quanto foi retido sobre o valor transacionado. Agrupamos por
// (parcelas, taxa) contando ocorrências e somando o valor transacionado.

export interface HeatSourceRow {
  parcelQtd?: number;
  totalValue?: number;
  compensationValue?: number;
  // NSU/código da transação. Presente = Link de pagamento; ausente = Máquina
  // física (correlação 100% validada nos dados). É o único sinal de canal
  // disponível na projeção rápida do /Quote/report.
  paymentCode?: string | number | null;
}

export type Channel = "all" | "maquina" | "link";

// Link de pagamento gera paymentCode (NSU do gateway); máquina física não.
export const isLinkPayment = (r: HeatSourceRow): boolean =>
  r.paymentCode != null && String(r.paymentCode).trim() !== "";

export function filterByChannel(rows: HeatSourceRow[], channel: Channel): HeatSourceRow[] {
  if (channel === "all") return rows;
  if (channel === "link") return rows.filter(isLinkPayment);
  return rows.filter((r) => !isLinkPayment(r));
}

export interface TaxaCellTotal {
  count: number;
  value: number;
}

export interface TaxaHeatmap {
  parcelas: number[]; // eixo Y, crescente
  taxas: number[]; // eixo X, crescente (arredondadas a 2 casas, em %)
  count: number[][]; // [parcelaIdx][taxaIdx]
  value: number[][]; // [parcelaIdx][taxaIdx]
  maxCount: number; // maior valor de célula (para a escala de cor)
  maxValue: number;
  totalCount: number;
  totalValueSum: number;
  parcelaTotals: TaxaCellTotal[]; // por linha
  taxaTotals: TaxaCellTotal[]; // por coluna
  topTaxas: { taxa: number; count: number; value: number }[]; // mais usadas
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function computeTaxaHeatmap(rows: HeatSourceRow[]): TaxaHeatmap {
  const parcelaSet = new Set<number>();
  const taxaSet = new Set<number>();
  const cell = new Map<string, TaxaCellTotal>(); // `${parcela}|${taxa}`
  let totalCount = 0;
  let totalValueSum = 0;

  for (const r of rows) {
    const tv = r.totalValue ?? 0;
    if (tv <= 0) continue;
    const parcela = r.parcelQtd && r.parcelQtd > 0 ? r.parcelQtd : 1;
    const comp = r.compensationValue ?? 0;
    let taxa = round2(((tv - comp) / tv) * 100);
    if (taxa < 0) taxa = 0;

    parcelaSet.add(parcela);
    taxaSet.add(taxa);
    const key = `${parcela}|${taxa}`;
    const cur = cell.get(key) ?? { count: 0, value: 0 };
    cur.count += 1;
    cur.value += tv;
    cell.set(key, cur);
    totalCount += 1;
    totalValueSum += tv;
  }

  const parcelas = Array.from(parcelaSet).sort((a, b) => a - b);
  const taxas = Array.from(taxaSet).sort((a, b) => a - b);
  const count = parcelas.map(() => taxas.map(() => 0));
  const value = parcelas.map(() => taxas.map(() => 0));
  const parcelaTotals: TaxaCellTotal[] = parcelas.map(() => ({ count: 0, value: 0 }));
  const taxaTotals: TaxaCellTotal[] = taxas.map(() => ({ count: 0, value: 0 }));
  let maxCount = 0;
  let maxValue = 0;

  parcelas.forEach((p, pi) => {
    taxas.forEach((t, ti) => {
      const cur = cell.get(`${p}|${t}`);
      if (!cur) return;
      count[pi][ti] = cur.count;
      value[pi][ti] = cur.value;
      parcelaTotals[pi].count += cur.count;
      parcelaTotals[pi].value += cur.value;
      taxaTotals[ti].count += cur.count;
      taxaTotals[ti].value += cur.value;
      if (cur.count > maxCount) maxCount = cur.count;
      if (cur.value > maxValue) maxValue = cur.value;
    });
  });

  const topTaxas = taxas
    .map((t, ti) => ({ taxa: t, count: taxaTotals[ti].count, value: taxaTotals[ti].value }))
    .sort((a, b) => b.count - a.count || b.value - a.value)
    .slice(0, 6);

  return {
    parcelas,
    taxas,
    count,
    value,
    maxCount,
    maxValue,
    totalCount,
    totalValueSum,
    parcelaTotals,
    taxaTotals,
    topTaxas,
  };
}
